import {
  Avatar,
  Checkbox,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { IMedication, MEDICATION_STATUSES } from 'API';
import {
  CustomMetaTitle,
  LinkReturnToMain,
  Loader,
  NextPrevButton,
} from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useDictionary, useIsDesktop, useMedications } from 'Hooks';
import React, { useContext, useEffect, useState } from 'react';
import { OrderRegularMedicationsContext } from '../OrderRegularMedicationsContext';
import { MedicationListItem } from 'Components/MedicationListItem';
import { PurpleChecked, PurpleUnchecked } from 'Images';
import { HeaderTitle } from './HeaderTitle';
import { NewUserInformation } from '../MedicationsAndPrescriptionsMain/NewUserInformation';

export const SelectMedications = () => {
  const {
    checkedMedications,
    checkAll,
    checkAllMedications,
    unCheckAllMedications,
    addCheckedMedication,
    removeCheckedMedication,
    nextStep,
    outOfStockMedications,
  } = useContext(OrderRegularMedicationsContext);

  const { medications } = useMedications();

  const dictionary = useDictionary(
    Dictionary,
    DICTIONARY.MedicationsAndPrescriptionsLobby
  );

  const isDesktop = useIsDesktop();
  const [isLoading, setIsLoading] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  const generalDictionary = useDictionary(Dictionary, DICTIONARY.General);
  const handleToggle = (medication: IMedication) => (e: React.MouseEvent) => {
    if (!isDesktop) e.stopPropagation();

    let prevIndex = -1;

    if (medication.medstatus === MEDICATION_STATUSES.notAvailable) {
      prevIndex = outOfStockMedications.findIndex(
        (prevOutOfStockMedication) =>
          prevOutOfStockMedication.medicatioN_CODE ===
          medication.medicatioN_CODE
      );
    } else {
      prevIndex = checkedMedications.findIndex(
        (prevCheckedMedication) =>
          prevCheckedMedication.medicatioN_CODE === medication.medicatioN_CODE
      );
    }

    if (prevIndex === -1) {
      addCheckedMedication(medication);
    } else {
      removeCheckedMedication(medication);
    }
  };

  const checkAllHandler = () => {
    checkAllMedications(medications);
  };

  const checkAllCheckboxHandler = () => {
    if (!checkAll) {
      checkAllHandler();
    } else {
      unCheckAllMedications();
    }
  };

  useEffect(() => {
    if (!isDesktop) {
      const handleScroll = () => {
        if (window.scrollY > 550) {
          setScrolled(true);
        } else {
          setScrolled(false);
        }
      };
      window.addEventListener('scroll', handleScroll);
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, [isDesktop]);

  useEffect(() => {
    if (medications && medications.length > 0) setIsLoading(false);
  }, [medications]);

  return (
    <>
      <CustomMetaTitle
        title={dictionary.title + ' - ' + dictionary.createOrder.title}
      />

      {isLoading ? (
        <Loader />
      ) : medications.length === 0 ? (
        <NewUserInformation isNoValidrMedications={true} />
      ) : (
        <>
          <LinkReturnToMain
            label={dictionary.createOrder.breadcrumbs}
            link='/medicine'
            internal={true}
          />
          <HeaderTitle />

          <Stack sx={{ maxWidth: '960px', pb: 2 }}>
            <Stack>
              <fieldset
                style={{
                  padding: 0,
                  margin: 0,
                  marginBottom: '24px',
                  border: 'none',
                  maxHeight: 'calc(100% - 24px)',
                }}
              >
                <Container sx={isDesktop ? undefined : { p: 0 }}>
                  <List sx={isDesktop ? undefined : { pb: 0 }}>
                    <ListItem
                      sx={{
                        mt: 3,
                        p: isDesktop ? undefined : '17px 7px',
                        height: isDesktop ? undefined : '64px',
                        pl: isDesktop ? 2 : 0,
                      }}
                    >
                      <Stack
                        tabIndex={1}
                        direction='row'
                        alignItems='center'
                        p={isDesktop ? '4px 24px' : undefined}
                        onClick={checkAllCheckboxHandler}
                        sx={{ cursor: 'pointer', width: '45%' }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: isDesktop ? undefined : 'unset',
                          }}
                        >
                          <Checkbox
                            icon={
                              <Avatar
                                alt=''
                                src={PurpleUnchecked}
                                sx={{ width: '24px', height: '24px' }}
                              />
                            }
                            checkedIcon={
                              <Avatar
                                alt=''
                                src={PurpleChecked}
                                sx={{ width: '24px', height: '24px' }}
                              />
                            }
                            sx={{
                              m: isDesktop ? undefined : 0,
                              height: isDesktop ? undefined : '18px',
                              width: isDesktop ? undefined : '18px',
                            }}
                            edge='start'
                            checked={checkAll}
                            tabIndex={-1}
                            disableRipple
                            inputProps={{ id: 'check-all' }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <label
                              style={{
                                pointerEvents: 'none',
                                fontSize: isDesktop ? '24px' : undefined,
                                fontWeight: isDesktop ? 'bold' : undefined,
                                color: '#344266',
                              }}
                              htmlFor='check-all'
                            >
                              {dictionary.checkAll}
                            </label>
                          }
                          sx={{
                            ml: isDesktop ? undefined : '9px',
                          }}
                        />
                      </Stack>

                      {isDesktop && (
                        <>
                          <Typography variant='body3' sx={{ width: '15%' }}>
                            {dictionary.units}
                          </Typography>
                          <Typography variant='body3' sx={{ width: '15%' }}>
                            {dictionary.dosageLabel}
                          </Typography>
                          <Typography variant='body3' sx={{ width: '20%' }}>
                            {dictionary.days}
                          </Typography>
                        </>
                      )}
                    </ListItem>
                    {medications.map((medication, index) => (
                      <MedicationListItem
                        key={medication.medicatioN_NAME}
                        checkedMedications={checkedMedications}
                        medication={medication}
                        outOfStockMedications={outOfStockMedications}
                        handleToggle={handleToggle}
                        showMedicationStatus={false}
                        index={index}
                      />
                    ))}
                  </List>
                </Container>
                {isDesktop && (
                  <Typography color='#3A556C' ml='8px'>
                    {checkedMedications.length !== 0 ||
                    outOfStockMedications.length !== 0
                      ? dictionary.youChoseNumberOfMedicationsDesktop(
                          checkedMedications.length +
                            outOfStockMedications.length
                        )
                      : null}
                  </Typography>
                )}
              </fieldset>
              <Container
                sx={
                  isDesktop
                    ? {
                        position: 'absolute',
                        bottom: '50px',
                        right: '40px',
                        direction: 'rtl',
                      }
                    : {
                        p: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }
                }
              ></Container>
            </Stack>
            <Container
              sx={
                isDesktop
                  ? {
                      display: 'flex',
                      flexDirection: 'row-reverse',
                    }
                  : {
                      p: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }
              }
            >
              {!isDesktop && checkedMedications.length > 0 && (
                <>
                  <Stack
                    sx={{
                      backgroundColor: '#fff',
                      mb: checkedMedications.length !== 0 ? '114px' : 10,
                      position: scrolled ? 'fixed' : undefined,
                      transition: 'transform 0.5s',
                      transform: scrolled
                        ? 'translateY(100%)'
                        : 'translateY(0)',
                      bottom: scrolled ? 0 : undefined,
                      zIndex: 9999,
                      gap: '10px',
                      padding: '20px 0',
                      alignItems: 'center',
                      ml: isDesktop ? -3 : undefined,
                    }}
                  >
                    {checkedMedications.length !== 0 ||
                    outOfStockMedications.length !== 0
                      ? dictionary.youChoseNumberOfMedicationsMobile(
                          checkedMedications.length +
                            outOfStockMedications.length
                        )
                      : null}

                    <NextPrevButton
                      callback={nextStep}
                      isDisabled={
                        checkedMedications.length === 0 &&
                        outOfStockMedications.length === 0
                      }
                      text={generalDictionary.continueToOrder}
                      isBlue
                      isNext
                      style={{
                        button: {
                          maxWidth: scrolled ? '200px' : undefined,
                          backgroundColor: 'primary.shade_400',
                          ':disabled': {
                            backgroundColor: '#526E9D',
                            color: 'white',
                          },
                          zIndex: 1,
                        },
                      }}
                    />
                  </Stack>
                </>
              )}
              {isDesktop && (
                <NextPrevButton
                  callback={nextStep}
                  isDisabled={
                    checkedMedications.length === 0 &&
                    outOfStockMedications.length === 0
                  }
                  text={generalDictionary.continueToOrder}
                  isBlue
                  isNext
                  style={{
                    button: {
                      mr: 2,
                      maxWidth: scrolled ? '200px' : undefined,
                      backgroundColor: 'primary.shade_400',
                      ':disabled': {
                        backgroundColor: '#526E9D',
                        color: 'white',
                      },
                      zIndex: 1,
                    },
                  }}
                />
              )}
            </Container>
          </Stack>
        </>
      )}
    </>
  );
};
